import React, { useState, useEffect } from 'react';
import { Heart, MessageCircle, Bookmark, Share2, ShoppingCart, ArrowLeft, Play, Pause, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import CommentsModal from '@/components/CommentsModal';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/providers/AuthProvider';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { useCart } from '@/contexts/CartContext';

interface Post {
  id: string;
  user_id: string;
  content_type: string;
  media_urls: string[];
  thumbnail_url?: string;
  caption: string | null;
  likes_count: number;
  comments_count: number;
  saves_count: number;
  created_at: string;
  profiles: {
    full_name: string;
    role: string;
    avatar_url: string;
    is_verified: boolean;
  } | null;
  restaurants: {
    id: string;
    name: string;
  } | null;
  menu_items: {
    id: string;
    name: string;
    price: number;
    image_url: string;
  } | null;
}

interface PostViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  post: Post | null;
  onBack: () => void;
  showDeleteButton?: boolean;
  onDelete?: (post: Post) => void;
}

const PostViewModal: React.FC<PostViewModalProps> = ({ isOpen, onClose, post, onBack, showDeleteButton = false, onDelete }) => {
  const [isLiked, setIsLiked] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [commentsCount, setCommentsCount] = useState(0);
  const [savesCount, setSavesCount] = useState(0);
  const [commentsModal, setCommentsModal] = useState(false);
  const [sharePost, setSharePost] = useState<Post | null>(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { addItem } = useCart();

  useEffect(() => {
    if (post && user) {
      setLikesCount(post.likes_count);
      setCommentsCount(post.comments_count);
      setSavesCount(post.saves_count);
      checkUserInteractions();
    }
  }, [post, user]);

  const checkUserInteractions = async () => {
    if (!post || !user) return;

    try {
      const [likesResult, savesResult] = await Promise.all([
        supabase
          .from('post_likes')
          .select('id')
          .eq('post_id', post.id)
          .eq('user_id', user.id)
          .single(),
        supabase
          .from('post_saves')
          .select('id')
          .eq('post_id', post.id)
          .eq('user_id', user.id)
          .single()
      ]);

      setIsLiked(!!likesResult.data);
      setIsSaved(!!savesResult.data);
    } catch (error) {
      // Expected errors when user hasn't liked/saved
    }
  };

  const handleLike = async () => {
    if (!user || !post) {
      toast({
        title: "Please log in",
        description: "You need to be logged in to like posts",
        variant: "destructive",
      });
      return;
    }

    try {
      if (isLiked) {
        await supabase
          .from('post_likes')
          .delete()
          .eq('post_id', post.id)
          .eq('user_id', user.id);
        
        setIsLiked(false);
        setLikesCount(prev => prev - 1);
      } else {
        await supabase
          .from('post_likes')
          .insert({ post_id: post.id, user_id: user.id });
        
        setIsLiked(true);
        setLikesCount(prev => prev + 1);
        
        if (navigator.vibrate) {
          navigator.vibrate(50);
        }
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  const handleSave = async () => {
    if (!user || !post) {
      toast({
        title: "Please log in",
        description: "You need to be logged in to save posts",
        variant: "destructive",
      });
      return;
    }

    try {
      if (isSaved) {
        await supabase
          .from('post_saves')
          .delete()
          .eq('post_id', post.id)
          .eq('user_id', user.id);
        
        setIsSaved(false);
        setSavesCount(prev => prev - 1);
        
        toast({
          title: "Post removed",
          description: "Post removed from your saved collection",
        });
      } else {
        await supabase
          .from('post_saves')
          .insert({ post_id: post.id, user_id: user.id });
        
        setIsSaved(true);
        setSavesCount(prev => prev + 1);
        
        toast({
          title: "Post saved",
          description: "Post has been saved to your collection",
        });
      }
    } catch (error) {
      console.error('Error toggling save:', error);
    }
  };

  const handleAddToCart = async () => {
    if (!user || !post) {
      toast({
        title: "Please log in",
        description: "You need to be logged in to add items to cart",
        variant: "destructive",
      });
      return;
    }

    if (!post.menu_items || !post.restaurants) {
      toast({
        title: "Item unavailable",
        description: "This item is not available for ordering",
        variant: "destructive",
      });
      return;
    }

    try {
      const { data: menuItemData } = await supabase
        .from('menu_items')
        .select('image_url, is_customizable')
        .eq('id', post.menu_items.id)
        .single();

      if (menuItemData?.is_customizable) {
        navigate(`/restaurant/${post.restaurants.id}?item=${post.menu_items.id}`);
        onClose();
        return;
      }

      await addItem({
        menu_item_id: post.menu_items.id,
        name: post.menu_items.name,
        price: post.menu_items.price,
        restaurant_id: post.restaurants.id,
        restaurant_name: post.restaurants.name,
        image_url: menuItemData?.image_url || null,
        referral_post_id: post.id,
        cart_type: 'restaurant',
      });


      navigate(`/restaurant/${post.restaurants.id}`);
      onClose();
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast({
        title: "Error",
        description: "Failed to add item to cart",
        variant: "destructive",
      });
    }
  };

  const handleShare = () => {
    setSharePost(post);
  };

  const shareToWhatsApp = () => {
    if (post) {
      const text = `Check out this post: ${post.caption || 'Amazing content!'} ${window.location.origin}/profile/${post.user_id}`;
      const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
      window.open(url, '_blank');
    }
  };

  const shareToFacebook = () => {
    if (post) {
      const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.origin + '/profile/' + post.user_id)}`;
      window.open(url, '_blank');
    }
  };

  const handleCopyLink = () => {
    if (post) {
      navigator.clipboard.writeText(`${window.location.origin}/profile/${post.user_id}`);
      toast({
        title: "Link copied",
        description: "Post link copied to clipboard",
      });
      setSharePost(null);
    }
  };

  const handleDelete = () => {
    if (post && onDelete) {
      onDelete(post);
      onClose();
    }
  };

  const toggleVideoPlay = (videoElement: HTMLVideoElement) => {
    if (isPlaying) {
      videoElement.pause();
      setIsPlaying(false);
    } else {
      videoElement.play();
      setIsPlaying(true);
    }
  };

  if (!post) return null;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="p-0 max-w-md w-full h-screen max-h-screen bg-black border-0">
          <div className="relative h-full flex flex-col">
            {/* Header */}
            <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between p-4 bg-gradient-to-b from-black/70 to-transparent">
              <Button
                variant="ghost"
                size="icon"
                onClick={onBack}
                className="text-white hover:bg-white/20 h-10 w-10"
              >
                <ArrowLeft className="h-6 w-6" />
              </Button>
              
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center overflow-hidden">
                  {post.profiles?.avatar_url ? (
                    <img 
                      src={post.profiles.avatar_url} 
                      alt="Profile" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-white text-sm font-bold">
                      {post.profiles?.full_name?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-white font-medium">{post.profiles?.full_name}</span>
                  {post.profiles?.is_verified && (
                    <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs">✓</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Media Content */}
            <div className="flex-1 flex items-center justify-center bg-black">
              {post.content_type === 'video' ? (
                <div className="relative w-full h-full">
                  <video
                    className="w-full h-full object-contain"
                    loop
                    muted
                    playsInline
                    autoPlay
                    onClick={(e) => toggleVideoPlay(e.currentTarget)}
                  >
                    <source src={post.media_urls[0]} type="video/mp4" />
                  </video>
                  {!isPlaying && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 bg-black/50 rounded-full flex items-center justify-center">
                        <Play className="h-8 w-8 text-white ml-1" />
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <img
                  src={post.media_urls[0]}
                  alt="Post"
                  className="w-full h-full object-contain"
                />
              )}
            </div>

            {/* Actions Bar */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-6">
                  <button
                    onClick={handleLike}
                    className="flex items-center gap-2 text-white hover:scale-110 transition-transform"
                  >
                    <Heart 
                      className={`h-7 w-7 ${isLiked ? 'fill-red-500 text-red-500' : ''}`} 
                    />
                    <span className="text-sm font-medium">{likesCount}</span>
                  </button>
                  
                  <button
                    onClick={() => setCommentsModal(true)}
                    className="flex items-center gap-2 text-white hover:scale-110 transition-transform"
                  >
                    <MessageCircle className="h-7 w-7" />
                    <span className="text-sm font-medium">{commentsCount}</span>
                  </button>
                  
                  <button
                    onClick={handleSave}
                    className="text-white hover:scale-110 transition-transform"
                  >
                    <Bookmark 
                      className={`h-7 w-7 ${isSaved ? 'fill-white' : ''}`} 
                    />
                  </button>
                </div>

                <div className="flex items-center gap-6">
                  {showDeleteButton ? (
                    <button
                      onClick={handleDelete}
                      className="text-white hover:scale-110 transition-transform bg-red-600 p-2 rounded-full"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  ) : (
                    post.menu_items && (
                      <button
                        onClick={handleAddToCart}
                        className="text-white hover:scale-110 transition-transform bg-red-600 p-2 rounded-full"
                      >
                        <ShoppingCart className="h-5 w-5" />
                      </button>
                    )
                  )}
                  
                  <button
                    onClick={handleShare}
                    className="text-white hover:scale-110 transition-transform"
                  >
                    <Share2 className="h-7 w-7" />
                  </button>
                </div>
              </div>

              {/* Caption */}
              {post.caption && (
                <div className="mt-4">
                  <p className="text-white text-sm leading-relaxed">{post.caption}</p>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Comments Modal */}
      {commentsModal && (
        <CommentsModal
          isOpen={commentsModal}
          onClose={() => setCommentsModal(false)}
          postId={post.id}
        />
      )}

      {/* Share Sheet */}
      {sharePost && (
        <Sheet open={!!sharePost} onOpenChange={() => setSharePost(null)}>
          <SheetContent side="bottom" className="h-auto">
            <div className="py-4">
              <h3 className="text-lg font-semibold mb-4">Share this post</h3>
              <div className="grid grid-cols-3 gap-4">
                <button
                  onClick={shareToWhatsApp}
                  className="flex flex-col items-center gap-2 p-4 rounded-lg hover:bg-gray-100"
                >
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold">W</span>
                  </div>
                  <span className="text-sm">WhatsApp</span>
                </button>
                
                <button
                  onClick={shareToFacebook}
                  className="flex flex-col items-center gap-2 p-4 rounded-lg hover:bg-gray-100"
                >
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold">f</span>
                  </div>
                  <span className="text-sm">Facebook</span>
                </button>
                
                <button
                  onClick={handleCopyLink}
                  className="flex flex-col items-center gap-2 p-4 rounded-lg hover:bg-gray-100"
                >
                  <div className="w-12 h-12 bg-gray-500 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold">🔗</span>
                  </div>
                  <span className="text-sm">Copy Link</span>
                </button>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      )}
    </>
  );
};

export default PostViewModal;